import { useState } from 'react';
import {
  Text,
  Button,
  Stack,
  Group,
  TextInput,
  ActionIcon,
  Box,
  Textarea,
  Badge,
  TagsInput,
  Accordion,
  Popover,
} from '@mantine/core';
import { IconX, IconPlus, IconAdjustments, IconCalendarStar, IconCarrot, IconTagStarred, IconWorldStar, IconInfoCircle } from '@tabler/icons-react';
import { DrawerHeader } from '../UI/DrawerHeader';
import { ResponsiveDrawer, useIsBottomSheet } from '../UI/ResponsiveDrawer';
import { ResponsiveSelect } from '../UI/ResponsiveSelect';
import { GroceryItem, ProductPreferences } from '../../types/mealPlan';

interface ShoppingCustomizationDrawerProps {
  opened: boolean;
  onClose: () => void;
  items: string[];
  onSave: (items: string[], productMatchingInstructions?: string, productPreferenceOverrides?: ProductPreferences) => void;
  loading?: boolean;
  isKrogerConnected?: boolean;
  productPreferences?: ProductPreferences;
  initialProductMatchingInstructions?: string;
  initialProductPreferenceOverrides?: ProductPreferences;
  groceryList?: GroceryItem[];
}

export const ShoppingCustomizationDrawer = ({
  opened,
  onClose,
  items,
  onSave,
  loading = false,
  isKrogerConnected = false,
  productPreferences,
  initialProductMatchingInstructions = '',
  initialProductPreferenceOverrides = {},
  groceryList = [],
}: ShoppingCustomizationDrawerProps) => {
  const isBottomSheet = useIsBottomSheet();
  const [localItems, setLocalItems] = useState<string[]>(items);
  const [newItem, setNewItem] = useState('');
  const [productMatchingInstructions, setProductMatchingInstructions] = useState(initialProductMatchingInstructions);
  const [productPreferenceOverrides, setProductPreferenceOverrides] = useState(initialProductPreferenceOverrides);

  const handleAddItem = () => {
    if (newItem.trim()) {
      const items = newItem.split(',').map(item => item.trim()).filter(item => item !== '');
      setLocalItems([...localItems, ...items]);
      setNewItem('');
    }
  };

  const handleRemoveItem = (index: number) => {
    setLocalItems(localItems.filter((_, i) => i !== index));
  };


  const handleSave = () => {
    const filteredItems = localItems.filter(item => item.trim() !== '');
    
    // If Kroger is connected, determine what to save for preferences
    let finalPreferenceOverrides = undefined;
    
    if (isKrogerConnected) {
      // Get the current effective values from the UI
      const currentPrefs = {
        brandPreference: productPreferenceOverrides.brandPreference || productPreferences?.brandPreference || 'no_preference',
        organicPreference: productPreferenceOverrides.organicPreference || productPreferences?.organicPreference || 'no_preference',
        foodHealthPreference: productPreferenceOverrides.foodHealthPreference || productPreferences?.foodHealthPreference || 'no_preference',
        pricePreference: productPreferenceOverrides.pricePreference || productPreferences?.pricePreference || 'no_preference',
        specificBrands: productPreferenceOverrides.specificBrands || productPreferences?.specificBrands || [],
        avoidBrands: productPreferenceOverrides.avoidBrands || productPreferences?.avoidBrands || []
      };
      
      // Helper function to compare arrays
      const arraysEqual = (a: string[], b: string[]) => {
        return a.length === b.length && a.every((val, i) => val === b[i]);
      };
      
      // Check if any preference differs from global default
      const hasOverrides =
        currentPrefs.brandPreference !== (productPreferences?.brandPreference || 'no_preference') ||
        currentPrefs.organicPreference !== (productPreferences?.organicPreference || 'no_preference') ||
        currentPrefs.foodHealthPreference !== (productPreferences?.foodHealthPreference || 'no_preference') ||
        currentPrefs.pricePreference !== (productPreferences?.pricePreference || 'no_preference') ||
        !arraysEqual(currentPrefs.specificBrands, productPreferences?.specificBrands || []) ||
        !arraysEqual(currentPrefs.avoidBrands, productPreferences?.avoidBrands || []);
        
      if (hasOverrides) {
        // Save only the preferences that actually differ from defaults
        const overridesOnly: any = {};
        if (currentPrefs.brandPreference !== (productPreferences?.brandPreference || 'no_preference')) {
          overridesOnly.brandPreference = currentPrefs.brandPreference;
        }
        if (currentPrefs.organicPreference !== (productPreferences?.organicPreference || 'no_preference')) {
          overridesOnly.organicPreference = currentPrefs.organicPreference;
        }
        if (currentPrefs.foodHealthPreference !== (productPreferences?.foodHealthPreference || 'no_preference')) {
          overridesOnly.foodHealthPreference = currentPrefs.foodHealthPreference;
        }
        if (currentPrefs.pricePreference !== (productPreferences?.pricePreference || 'no_preference')) {
          overridesOnly.pricePreference = currentPrefs.pricePreference;
        }
        if (!arraysEqual(currentPrefs.specificBrands, productPreferences?.specificBrands || [])) {
          overridesOnly.specificBrands = currentPrefs.specificBrands;
        }
        if (!arraysEqual(currentPrefs.avoidBrands, productPreferences?.avoidBrands || [])) {
          overridesOnly.avoidBrands = currentPrefs.avoidBrands;
        }
        finalPreferenceOverrides = overridesOnly;
      } else {
        // User has set everything back to defaults - explicitly clear overrides with empty object
        finalPreferenceOverrides = {};
      }
    }
    
    onSave(
      filteredItems, 
      productMatchingInstructions.trim() || undefined,
      finalPreferenceOverrides
    );
  };

  const handleCancel = () => {
    setLocalItems(items); // Reset to original items
    setNewItem('');
    setProductMatchingInstructions(initialProductMatchingInstructions);
    setProductPreferenceOverrides(initialProductPreferenceOverrides);
    onClose();
  };

  return (
    <ResponsiveDrawer
      opened={opened}
      onClose={handleCancel}
      size="md"
      title={
        <DrawerHeader
          icon={<IconAdjustments size={18} />}
          title="Shopping customization"
          subtitle="Add groceries & customize preferences"
        />
      }
      styles={{
        header: {
          paddingBottom: 16,
        },
        body: {
          padding: 0,
        },
      }}
    >
      <Stack gap="md" p="md" h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
        <Box style={{ flex: 1, overflow: 'auto' }}>
          <Accordion
            variant="separated"
            radius="lg"
            defaultValue={!isKrogerConnected ? "additional-items" : undefined}
            styles={{
              // Paint the item (the element that carries the radius) and clip
              // its children; a background on control/panel pokes square
              // corners past the rounding
              item: {
                backgroundColor: 'var(--mantine-color-gray-0)',
                border: 'none',
                overflow: 'hidden',
              },
            }}
          >
            {/* Additional Items Section */}
            <Accordion.Item value="additional-items">
              <Accordion.Control>
                <Group justify="space-between" style={{ width: '100%', paddingRight: '1rem' }}>
                  <Text>Additional groceries</Text>
                  {localItems.length > 0 && (
                    <Badge size="sm" variant="light">{localItems.length}</Badge>
                  )}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  <Text size="xs" c="dimmed">
                    Add items like household products, snacks, or extra ingredients not included in your recipes
                  </Text>
                  
                  {/* Add new item input */}
                  <Stack gap="xs">
                    <Group gap="xs">
                      <TextInput
                        placeholder="2 bottles of dish soap, paper towels, eggs"
                        value={newItem}
                        onChange={(e) => setNewItem(e.currentTarget.value)}
                        style={{ flex: 1 }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleAddItem();
                          }
                        }}
                      />
                      <ActionIcon
                        variant="filled"
                        size="lg"
                        onClick={handleAddItem}
                        disabled={!newItem.trim()}
                      >
                        <IconPlus size={16} />
                      </ActionIcon>
                    </Group>
                  </Stack>

                  {/* Existing items list */}
                  {localItems.length > 0 && (
                    <Stack gap={0}>
                      {localItems.map((item, index) => (
                        <Box key={index} p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: '4px' }}>
                          <Group gap="xs" align="center">
                            <Text style={{ flex: 1 }} size="sm">
                              {item}
                            </Text>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="sm"
                              onClick={() => handleRemoveItem(index)}
                            >
                              <IconX size={14} />
                            </ActionIcon>
                          </Group>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            {/* Shopping Preferences Section - Only for Kroger users */}
            {isKrogerConnected && (
              <Accordion.Item value="shopping-preferences">
                <Accordion.Control>Shopping preferences</Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    <Text size="xs" c="dimmed">
                      Tell us your preferences to help pick the best products for {localItems.length > 0 ? 'your recipe ingredients and additional groceries' : 'you'}
                    </Text>
                    <Textarea
                      placeholder="I have sensitive skin, I prefer larger sizes, no artificial colors..."
                      value={productMatchingInstructions}
                      onChange={(e) => setProductMatchingInstructions(e.currentTarget.value)}
                      rows={3}
                      maxRows={4}
                    />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}

            {/* Product Preferences Section - Only for Kroger users */}
            {isKrogerConnected && (
              <Accordion.Item value="product-preferences">
                <Accordion.Control>Product preferences</Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    <Text size="xs" c="dimmed">
                      Customize preferences for this meal plan{localItems.length > 0 ? ' and your added groceries' : ''}
                    </Text>
                    
                    <Stack gap="sm">
                        <ResponsiveSelect
                          label="Brand preference"
                          data={[
                            { value: 'no_preference', label: 'No preference' },
                            { value: 'store', label: 'Prefer store brands' },
                            { value: 'name', label: 'Prefer name brands' }
                          ]}
                          value={productPreferenceOverrides.brandPreference || productPreferences?.brandPreference || 'no_preference'}
                          onChange={(value) => setProductPreferenceOverrides({
                            ...productPreferenceOverrides,
                            brandPreference: value as any
                          })}
                        />
                        
                        <ResponsiveSelect
                          label="Organic preference"
                          data={[
                            { value: 'no_preference', label: 'No preference' },
                            { value: 'prefer_organic', label: 'Prefer organic' }
                          ]}
                          value={productPreferenceOverrides.organicPreference || productPreferences?.organicPreference || 'no_preference'}
                          onChange={(value) => setProductPreferenceOverrides({
                            ...productPreferenceOverrides,
                            organicPreference: value as any
                          })}
                        />
                        
                        <Stack gap={4}>
                          {/* Inline icon glued to the last word: wraps with
                              "preference" instead of dropping below the label */}
                          <Text size="sm" fw={500}>
                            FoodHealth Score{' '}
                            <span style={{ whiteSpace: 'nowrap' }}>
                              preference
                              <Popover width={260} position="bottom" withArrow shadow="md" withinPortal={!isBottomSheet}>
                                <Popover.Target>
                                  <ActionIcon
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                    aria-label="What is FoodHealth Score?"
                                    style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: 6 }}
                                  >
                                    <IconInfoCircle size={14} />
                                  </ActionIcon>
                                </Popover.Target>
                                <Popover.Dropdown>
                                  <Text size="xs" c="gray.7" style={{ lineHeight: 1.4 }}>
                                    The <a href="https://www.foodhealth.co" target="_blank" rel="noreferrer" style={{ color: 'var(--mantine-color-blue-6)' }}>FoodHealth Score</a> rates products from 0–100 based on nutritional quality. When enabled, we'll favor higher-scoring products when choosing between similar options.
                                  </Text>
                                </Popover.Dropdown>
                              </Popover>
                            </span>
                          </Text>
                          <ResponsiveSelect
                            data={[
                              { value: 'no_preference', label: 'No preference' },
                              { value: 'prefer_higher', label: 'Prefer higher scores' }
                            ]}
                            value={productPreferenceOverrides.foodHealthPreference || productPreferences?.foodHealthPreference || 'no_preference'}
                            onChange={(value) => setProductPreferenceOverrides({
                              ...productPreferenceOverrides,
                              foodHealthPreference: value as any
                            })}
                          />
                        </Stack>

                        <ResponsiveSelect
                          label="Price preference"
                          data={[
                            { value: 'no_preference', label: 'No preference' },
                            { value: 'value', label: 'Value-focused' },
                            { value: 'mid_range', label: 'Mid-range pricing' },
                            { value: 'premium', label: 'Premium products' }
                          ]}
                          value={productPreferenceOverrides.pricePreference || productPreferences?.pricePreference || 'no_preference'}
                          onChange={(value) => setProductPreferenceOverrides({
                            ...productPreferenceOverrides,
                            pricePreference: value as any
                          })}
                        />
                        
                        <TagsInput
                          label="Preferred brands"
                          placeholder="Heinz, Private Selection, Simple Truth"
                          value={productPreferenceOverrides.specificBrands || productPreferences?.specificBrands || []}
                          onChange={(value) => setProductPreferenceOverrides({
                            ...productPreferenceOverrides,
                            specificBrands: value
                          })}
                          clearable
                          comboboxProps={{ withinPortal: !isBottomSheet }}
                        />

                        <TagsInput
                          label="Brands to avoid"
                          placeholder="Heinz, Private Selection, Simple Truth"
                          value={productPreferenceOverrides.avoidBrands || productPreferences?.avoidBrands || []}
                          onChange={(value) => setProductPreferenceOverrides({
                            ...productPreferenceOverrides,
                            avoidBrands: value
                          })}
                          clearable
                          comboboxProps={{ withinPortal: !isBottomSheet }}
                        />
                      </Stack>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}
          </Accordion>
        </Box>

        {/* Current Product Preferences Context - Only for Kroger users */}
        {isKrogerConnected && groceryList.length > 0 && (() => {
          const itemsWithPreferences = groceryList.filter(item => item.productPreference);
          
          // Group by scope and preference
          const ingredientScoped = itemsWithPreferences.filter(item => item.productPreferenceScope === 'ingredient');
          const categoryScoped = itemsWithPreferences.filter(item => item.productPreferenceScope === 'category');
          const globalScoped = itemsWithPreferences.filter(item => item.productPreferenceScope === 'global');
          const mealPlanScoped = itemsWithPreferences.filter(item => !item.productPreferenceScope);
          
          
          // For categories, group by category and preference combination
          const categoryGroups = categoryScoped.reduce((acc, item) => {
            const key = `${item.category}-${item.productPreference}`;
            if (!acc[key]) {
              acc[key] = {
                category: item.category,
                preference: item.productPreference,
                items: []
              };
            }
            acc[key].items.push(item.itemName);
            return acc;
          }, {} as Record<string, { category?: string, preference?: string, items: string[] }>);
          
          // For global, group by preference
          const globalGroups = globalScoped.reduce((acc, item) => {
            const key = item.productPreference || '';
            if (!acc[key]) {
              acc[key] = {
                preference: item.productPreference,
                items: []
              };
            }
            acc[key].items.push(item.itemName);
            return acc;
          }, {} as Record<string, { preference?: string, items: string[] }>);
          
          const hasAnyPreferences = ingredientScoped.length > 0 || Object.keys(categoryGroups).length > 0 || Object.keys(globalGroups).length > 0 || mealPlanScoped.length > 0;
          
          return hasAnyPreferences ? (
            <Box p="md" style={{ backgroundColor: 'var(--mantine-color-white)', borderRadius: 'var(--mantine-radius-sm)' }}>
              <Text mb="xs">Meal plan item preferences</Text>
              <Text size="xs" c="dimmed" mb="sm">Active preferences from your grocery list:</Text>
              <Stack gap="xs">
                {/* Meal Plan-scoped preferences (show individually) */}
                {mealPlanScoped.slice(0, 8).map((item, index) => (
                  <Box key={`mealplan-${index}`} p="xs" style={{ backgroundColor: 'var(--mantine-color-white)', borderRadius: '4px', border: '1px solid var(--mantine-color-gray-3)' }}>
                    <Group gap="xs" align="flex-start">
                      <IconCalendarStar size={14} style={{ color: '#134B97', opacity: 0.7, flexShrink: 0, marginTop: '1px' }} />
                      <Text size="xs" fw={500} style={{ flex: 1 }}>
                        {item.itemName}
                      </Text>
                      <Text size="xs" c="blue.6" style={{ fontStyle: 'italic' }}>
                        "{item.productPreference}"
                      </Text>
                    </Group>
                  </Box>
                ))}
                
                {/* Ingredient-scoped preferences (show individually) */}
                {ingredientScoped.slice(0, 8).map((item, index) => (
                  <Box key={`ingredient-${index}`} p="xs" style={{ backgroundColor: 'var(--mantine-color-white)', borderRadius: '4px', border: '1px solid var(--mantine-color-gray-3)' }}>
                    <Group gap="xs" align="flex-start">
                      <IconCarrot size={14} style={{ color: '#134B97', opacity: 0.7, flexShrink: 0, marginTop: '1px' }} />
                      <Text size="xs" fw={500} style={{ flex: 1 }}>
                        {item.itemName}
                      </Text>
                      <Text size="xs" c="blue.6" style={{ fontStyle: 'italic' }}>
                        "{item.productPreference}"
                      </Text>
                    </Group>
                  </Box>
                ))}
                
                {/* Category-scoped preferences (show once per category+preference combo) */}
                {Object.values(categoryGroups).map((group, index) => (
                  <Box key={`category-${index}`} p="xs" style={{ backgroundColor: 'var(--mantine-color-white)', borderRadius: '4px', border: '1px solid var(--mantine-color-gray-3)' }}>
                    <Group gap="xs" align="flex-start">
                      <IconTagStarred size={14} style={{ color: '#134B97', opacity: 0.7, flexShrink: 0, marginTop: '1px' }} />
                      <Text size="xs" fw={500} style={{ flex: 1 }}>
                        {group.category || 'Category'} items ({group.items.length})
                      </Text>
                      <Text size="xs" c="blue.6" style={{ fontStyle: 'italic' }}>
                        "{group.preference}"
                      </Text>
                    </Group>
                  </Box>
                ))}
                
                {/* Global-scoped preferences (show once per preference) */}
                {Object.values(globalGroups).map((group, index) => (
                  <Box key={`global-${index}`} p="xs" style={{ backgroundColor: 'var(--mantine-color-white)', borderRadius: '4px', border: '1px solid var(--mantine-color-gray-3)' }}>
                    <Group gap="xs" align="flex-start">
                      <IconWorldStar size={14} style={{ color: '#134B97', opacity: 0.7, flexShrink: 0, marginTop: '1px' }} />
                      <Text size="xs" fw={500} style={{ flex: 1 }}>
                        All items ({group.items.length})
                      </Text>
                      <Text size="xs" c="blue.6" style={{ fontStyle: 'italic' }}>
                        "{group.preference}"
                      </Text>
                    </Group>
                  </Box>
                ))}
                
                {ingredientScoped.length > 8 && (
                  <Text size="xs" c="dimmed" ta="center">
                    ...and {ingredientScoped.length - 8} more ingredient preferences
                  </Text>
                )}
              </Stack>
            </Box>
          ) : null;
        })()}

        {/* Footer actions */}
        <Group gap="sm" justify="flex-end" p="md">
          <Button variant="subtle" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={loading}>
            Save customizations
          </Button>
        </Group>
      </Stack>
    </ResponsiveDrawer>
  );
};
